import { Medication } from "../models/procedure.model";
import { PhiProcedureMedicationRow } from "../models/phi-medications-reconciliation.model";
import { OrderType } from "../services/utils";

export const NO_KNOWN_MEDICATIONS_LABEL = "No Known Medications";

type MedicationNameLike =
  | { name?: string | null; commonName?: string | null }
  | null
  | undefined;

export function isNoKnownMedicationRecord(
  row: MedicationNameLike
): boolean {
  const name = String(row?.name ?? row?.commonName ?? "")
    .trim()
    .toLowerCase();
  return (
    name === "no known medications" ||
    name === "no known medication" ||
    name === "nkm"
  );
}

/**
 * Row inserted by the administer-medication sync. It belongs on Administer
 * Medication, not Medication Reconciliation.
 */
export function isAdministeredMedicationShadowRow(
  row: object | null | undefined
): boolean {
  if (!row) {
    return false;
  }
  const record = row as Record<string, unknown>;
  if (record["existing"] === true) {
    return false;
  }
  return (
    String(record["action"] ?? "").trim() === "Administered" &&
    String(record["medActionStatus"] ?? "").trim() === "Administered" &&
    String(record["type"] ?? "").trim() === "Required" &&
    String(record["orderType"] ?? "").trim() === "Procedure"
  );
}

/**
 * Plan Rx still at default workflow — only on the encounter plan list, not
 * reconciliation / History / patient-wide medication summaries.
 */
export type MedicationReconciliationFilterOptions = {
  currentEncounterId?: string | null;
};

export function medicationReconciliationOrderId(
  row: Medication | Record<string, unknown> | null | undefined,
): string {
  if (!row) {
    return "";
  }
  const record = row as Record<string, unknown>;
  return String(record["rxDisplayNumber"] ?? record["clinicalOrderId"] ?? "")
    .trim();
}

/** Encounter plan / workflow row — not a reconciled home (existing) medication. */
export function isEncounterPlanMedicationOrder(row: Medication): boolean {
  if (row.existing === true) {
    return false;
  }
  if (isNoKnownMedicationRecord(row) || isAdministeredMedicationShadowRow(row)) {
    return false;
  }
  const orderType = String(row.orderType ?? "")
    .trim()
    .toLowerCase();
  if (orderType === OrderType.prior.toLowerCase()) {
    return false;
  }
  return true;
}

/**
 * Plan medication orders belong on the encounter plan, not reconciliation — except
 * a prior encounter order that already has an order ID (RX display number).
 */
export function shouldExcludeEncounterPlanMedFromReconciliation(
  row: Medication,
  currentEncounterId?: string | null,
): boolean {
  if (!isEncounterPlanMedicationOrder(row)) {
    return false;
  }
  const current = String(currentEncounterId ?? "").trim();
  const rowEncounter = String(row.encounterId ?? "").trim();
  const orderId = medicationReconciliationOrderId(row);

  if (current && rowEncounter && rowEncounter === current) {
    return true;
  }
  if (!orderId && isOrderedPlanRxMedication(row)) {
    return true;
  }
  if (current && rowEncounter && rowEncounter !== current) {
    return !orderId;
  }
  if (!current) {
    return isOrderedPlanRxMedication(row) && !orderId;
  }
  return !orderId;
}

export function isOrderedPlanRxMedication(row: Medication): boolean {
  if (row.existing) {
    return false;
  }
  const orderType = String(row.orderType ?? "")
    .trim()
    .toLowerCase();
  if (orderType === OrderType.prior.toLowerCase()) {
    return false;
  }
  if (
    orderType &&
    orderType !== OrderType.rx.toLowerCase() &&
    orderType !== "discharge"
  ) {
    return false;
  }
  const raw = String(row.action ?? "").trim().toLowerCase();
  if (raw && raw !== "ordered") {
    return false;
  }
  return true;
}

/** Archived / cancelled plan Rx — omit from plan lists, SOAP, and encounter med cache. */
export function isCancelledEncounterMedication(
  row: Medication | null | undefined,
): boolean {
  if (!row) {
    return false;
  }
  const raw = String(row.action ?? "").trim().toLowerCase();
  return raw.includes("cancel");
}

/** Cancelled encounter plan Rx (not prior/home med reconciliation rows). */
export function isCancelledPlanRxMedication(
  row: Medication | null | undefined,
): boolean {
  if (!row || row.existing) {
    return false;
  }
  const orderType = String(row.orderType ?? "")
    .trim()
    .toLowerCase();
  if (
    orderType &&
    orderType !== OrderType.rx.toLowerCase() &&
    orderType !== "discharge"
  ) {
    return false;
  }
  return isCancelledEncounterMedication(row);
}

export function filterVisibleEncounterMedications<T extends Medication>(
  rows: T[] | null | undefined,
): T[] {
  return (rows ?? []).filter((row) => !isCancelledEncounterMedication(row));
}

/** Plan / new Rx rows for SOAP PLAN (not current-medication reconciliation). */
export function isEncounterPlanMedicationForNote(
  row: Medication | null | undefined,
): boolean {
  if (!row) {
    return false;
  }
  if (isCancelledEncounterMedication(row)) {
    return false;
  }
  if (row.existing === true) {
    return false;
  }
  if (row.isActive === false) {
    return false;
  }
  if (isNoKnownMedicationRecord(row)) {
    return false;
  }
  if (isAdministeredMedicationShadowRow(row)) {
    return false;
  }
  const orderType = String(row.orderType ?? "")
    .trim()
    .toLowerCase();
  if (orderType === OrderType.prior.toLowerCase()) {
    return false;
  }
  return true;
}

/** Medication rows shown in the reconciliation table (excludes the NKM sentinel row). */
export function medicationReconciliationListVisibleMeds(
  rows: Medication[] | null | undefined,
  options?: MedicationReconciliationFilterOptions,
): Medication[] {
  const currentEncounterId = options?.currentEncounterId ?? null;
  return (rows || []).filter(
    (row) =>
      !isNoKnownMedicationRecord(row) &&
      !isAdministeredMedicationShadowRow(row) &&
      !isOrderedPlanRxMedication(row) &&
      !shouldExcludeEncounterPlanMedFromReconciliation(row, currentEncounterId),
  );
}

export function medicationReconciliationForEncounter(
  rows: Medication[] | null | undefined,
  currentEncounterId?: string | null,
): Medication[] {
  return medicationReconciliationListVisibleMeds(rows, {
    currentEncounterId,
  });
}

/** Post-procedure PHI rows for UI/PDF (excludes the NKM sentinel row). */
export function procedurePhiMedicationRowsVisibleForDisplay(
  rows: PhiProcedureMedicationRow[] | null | undefined
): PhiProcedureMedicationRow[] {
  return (rows || []).filter(
    (row) =>
      !isNoKnownMedicationRecord(row) && !isAdministeredMedicationShadowRow(row)
  );
}

/** True when any source list contains the NKM sentinel row. */
export function hasNoKnownMedicationsDeclared(
  ...sources: Array<
    | Medication[]
    | PhiProcedureMedicationRow[]
    | null
    | undefined
  >
): boolean {
  return sources.some((source) =>
    (source || []).some((row) => isNoKnownMedicationRecord(row))
  );
}
